import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Shield, Lock, Building2, User, Briefcase } from "lucide-react";

const DEMO_ACCOUNTS = [
  { email: "admin@growthink.com", password: "Admin@123", role: "Admin", icon: Shield },
  { email: "investor@growthink.com", password: "Investor@123", role: "Investor", icon: User },
  { email: "developer@growthink.com", password: "Dev@123", role: "Developer", icon: Briefcase }
];

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success(`Welcome back, ${result.user.name}!`);
      navigate("/dashboard");
    } else {
      toast.error(result.error);
    }
    
    setIsLoading(false);
  };

  const handleDemoLogin = async (demoAccount) => {
    setEmail(demoAccount.email);
    setPassword(demoAccount.password);
    setIsLoading(true);

    const result = await login(demoAccount.email, demoAccount.password);
    
    if (result.success) {
      toast.success(`Welcome, ${result.user.name}! (${demoAccount.role} Demo)`);
      navigate("/dashboard");
    } else {
      toast.error(result.error);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="login-split" data-testid="login-page">
      {/* Left Brand Section */}
      <div className="login-brand">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Growthink</h1>
              <p className="text-white/60 text-sm">Tokenization Platform</p>
            </div>
          </div>
          
          <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
            Global First AI-Integrated<br />Security Asset-Backed<br />Tokenization Platform
          </h2>
          
          <p className="text-white/70 text-lg mb-8 max-w-md">
            Invest in tokenized real-world assets with AI-powered due diligence scoring and complete transparency.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold text-white">$98.6M</div>
              <div className="text-white/60 text-sm">Total Token Value</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold text-white">$986</div>
              <div className="text-white/60 text-sm">Per Token</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold text-white">3</div>
              <div className="text-white/60 text-sm">STO Projects</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold text-white">100K</div>
              <div className="text-white/60 text-sm">Tokens Per Project</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Form Section */}
      <div className="login-form">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Welcome Back</h2>
            <p className="text-slate-500">Sign in to access your dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                data-testid="login-email-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                data-testid="login-password-input"
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-[#0F2744] hover:bg-[#1E3A5F] text-white font-medium"
              disabled={isLoading}
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Sign In
                </span>
              )}
            </Button>
          </form>

          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500">Demo Accounts</span>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {DEMO_ACCOUNTS.map((account) => (
                <button
                  key={account.email}
                  onClick={() => handleDemoLogin(account)}
                  disabled={isLoading}
                  className="w-full flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-slate-50 transition-all group"
                  data-testid={`demo-login-${account.role.toLowerCase()}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center group-hover:bg-blue-50">
                      <account.icon className="w-5 h-5 text-slate-600 group-hover:text-blue-600" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-slate-900">{account.role} Demo</div>
                      <div className="text-sm text-slate-500">{account.email}</div>
                    </div>
                  </div>
                  <div className="text-sm text-blue-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Login →
                  </div>
                </button>
              ))}
            </div>
          </div>

          <p className="mt-8 text-center text-sm text-slate-500">
            This is a demo platform. All data is simulated.
          </p>
        </div>
      </div>
    </div>
  );
}
